import { EmailIcon, PhoneIcon } from '@chakra-ui/icons'
import { Box, Flex, Heading, Icon, Link, Skeleton, SkeletonText, Text, VStack } from '@chakra-ui/react'
import type { IImage, IHeading } from '@stocker/ui-components/design-system'
import { CustomHeading } from '@stocker/ui-components/helpers'
import NextImage from 'next/image'
import type React from 'react'
import { useState } from 'react'
import { AiOutlineMobile, AiOutlineUser } from 'react-icons/ai'

export interface IEmployeeCardProps {
  name?: string
  image?: IImage
  position?: string
  telephoneNumber?: string
  mobileNumber?: string
  email?: string
  heading?: IHeading
  isLoaded?: boolean
  positionInfo?: string
}
interface IColors {
  colorBorder?: string
  colorAccent?: string
}
interface ICombined extends IColors, IEmployeeCardProps {}

export const EmployeeCard: React.FC<ICombined> = ({
  name, image, position, telephoneNumber, mobileNumber, email, heading, isLoaded = true,
  colorBorder = 'secondaryBackground.550', colorAccent = 'accent.500',
  positionInfo,
}) => {
  const [imgError, setImgError] = useState(false)
  const photoSrc = image?.fullpath
  const showPhoto = !!photoSrc && !photoSrc.includes('filetype-not-supported') && !imgError
  return (
    <Box w="100%" textAlign="left">
      {heading && <CustomHeading heading={heading} mb="5px"/>}
      <VStack
        w="100%"
        justify="flex-start"
        align="flex-start"
        fontSize="md"
        spacing={1}
      >
        <Box
          position="relative"
          h="300px"
          // 4:5 at the fixed height, so the frame stops taking its shape from whatever
          // column width the consumer happens to give it — that is what indented the photo.
          sx={{ aspectRatio: '4 / 5' }}
          bg="white"
          overflow="hidden"
          mb={2}
        >
          {!isLoaded
            ? <Skeleton position="absolute" inset={0}/>
            : showPhoto
              ? (
                <NextImage
                  src={photoSrc!}
                  placeholder={image?.blurDataUrl ? 'blur' : 'empty'}
                  blurDataURL={image?.blurDataUrl}
                  fill
                  sizes="240px"
                  style={{
                    objectFit: 'contain',
                    // sources narrower than the 4:5 frame keep their slack on the right
                    objectPosition: 'left',
                  }}
                  alt="Employee Image"
                  onError={() => setImgError(true)}
                />
                )
              : (
                <Flex position="absolute" inset={0} align="center" justify="center">
                  <Icon as={AiOutlineUser} boxSize="64px" color="secondaryBackground.550"/>
                </Flex>
                )}
        </Box>
        {positionInfo && (
          <Skeleton isLoaded={isLoaded}>
            <Text>
              {positionInfo}
            </Text>
          </Skeleton>
        )}
        <Skeleton isLoaded={isLoaded}>
          <Heading
            as="h4"
            fontSize="xl"
            fontWeight="700"
          >{name}
          </Heading>
        </Skeleton>
        <Skeleton isLoaded={isLoaded} mt={-1}>
          <Text
            pb="2"
          >
            {position}
          </Text>
        </Skeleton>
        <SkeletonText noOfLines={3} isLoaded={isLoaded} minW="200px" spacing={4}/>
        {telephoneNumber && (
          <Link href={`tel:${telephoneNumber}`}>
            <Text
              color={colorAccent}
              fontWeight="medium"
            >
              <PhoneIcon color="secondaryText.500"/> {telephoneNumber}
            </Text>
          </Link>
        )}
        {mobileNumber && (
          <Link href={`tel:${mobileNumber}`}>
            <Text
              color={colorAccent}
              fontWeight="medium"
            >
              <Icon as={AiOutlineMobile} fontSize="lg" color="secondaryText.500"/>
              {mobileNumber}
            </Text>
          </Link>
        )}
        {email && (
          <Link href={`mailto:${email}`}>
            <Text
              color={colorAccent}
              fontWeight="medium"
            >
              <EmailIcon color="secondaryText.500"/>
              {` ${email}`}
            </Text>
          </Link>
        )}
      </VStack>
    </Box>
  )
}
